import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { Button, Combobox, Icon, Radio, RadioGroup, Textarea } from '@salesforce/design-system-react';
import { CONFERENCE_NAME } from '../../constants';
import { lunchTimes, mealOptions, drinkOptions, dessertOptions } from '../../data';
import BrowserContainer from '../../containers/browserContainer';

class ErrorText extends Component {
  render() {
    return (
      <div className='slds-has-error'>
        <div
          className='slds-form-element__help slds-has-error'
          id={this.props.idPrefix + '-error'}
        >
          {this.props.text}
        </div>
      </div>
    );
  }
}

class AccessibleCombobox extends Component {
  constructor (props) {
    super(props);

    this.state = {
      inputValue: '',
      pickupTime: null,
      selection: [],
      showError: false
    };
  }

  render() {
    return (
      <Combobox
        id={this.props.id}
        errorText={this.props.showError || this.state.showError ? this.props.errorText : null}
        events={{
          onSelect: (event, data) => {
            if (this.props.action) {
              this.props.action('onSelect')(
                event,
                ...Object.keys(data).map((key) => data[key])
              );
            }
            this.setState({
              inputValue: '',
              selection: data.selection,
            });
          },
        }}
        labels={{
          label: this.props.label,
          placeholder: this.props.placeholder,
        }}
        options={this.props.options}
        required={this.props.required}
        selection={this.state.selection}
        value={this.state.inputValue}
        variant='readonly'
      />
    );
  }
}

class InaccessibleCombobox extends Component {
  constructor (props) {
    super(props);

    this.state = {
      inputValue: '',
      selection: [],
      showError: false
    };
  }

  renderRequiredIndicator() {
    if (this.props.required) {
      return (
        <abbr className='slds-required' title='required'>*</abbr> 
      );
    }
  }

  renderErrorMessage() {
    if (this.props.showError || this.state.showError) {
      return <ErrorText idPrefix={this.props.id} text={this.props.errorText} />
    }
    return null;
  }

  render() {
    return (
      <div className='slds-form-element'>
        <label className='slds-form-element__label' htmlFor={this.props.id} >
          {this.renderRequiredIndicator()}{this.props.label}
        </label>

        <div className='slds-form-element__control'>
          <div className='slds-combobox_container'>
            <div
              className={classNames(
                'slds-combobox slds-dropdown-trigger',
                'slds-dropdown-trigger_click',
                'ignore-react-onclickoutside',
                {'slds-has-error': this.props.showError || this.state.showError})}
            >
              <div className='slds-combobox__form-element slds-input-has-icon slds-input-has-icon_right' role='none'>
                <input 
                  aria-describedby={this.state.showError ? this.props.id + '-error' : null}
                  autoComplete='off'
                  className='slds-input slds-combobox__input'
                  id={this.props.id}
                  placeholder={this.props.placeholder}
                  onBlur={() => {if (this.props.required) this.setState ({showError: true})}}
                  readOnly=''
                  required={this.props.required}
                  type='text'
                  value=''/>
                <Icon
                  category='utility'
                  name='down'
                  size='x-small'
                  containerClassName='slds-icon_container slds-input__icon slds-input__icon_right'
                  className=''
                />
              </div>
            </div>
          </div>
          {this.renderErrorMessage()}
        </div>
      </div>
    );
  } 
}

class LunchSelectorScreen extends Component {

  constructor (props) {
    super(props);

    this.state = {
      inputValue: '',
      selection: [],
      idSuffix: Math.floor(Math.random() * Math.floor(100)),
      showMealError: false,
      showDrinkError: false,
      pickupTime: '11:00',
      focusTrap: true,
    };

    this.continueButton = React.createRef();
    this.selectedRadio = React.createRef();
    this.drink = React.createRef();
    this.meal = React.createRef();
    this.submit = this.submit.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidMount() {
    // Focus radio as first thing
    ReactDOM.findDOMNode(this.selectedRadio.current).querySelector('input').focus();
  }

  renderPickupTime() {
    return (
      <div className='pts'>
        <RadioGroup
            labels={{ label: 'Pick-up Time' }}
            onChange={(event) => this.setState({ pickupTime: event.target.value })}
            required={true}
            name='pickupTime'
            className='radio-group-horizontal'
          >
            {lunchTimes.map((value) => (
              <Radio
                ref={this.state.pickupTime === value ? this.selectedRadio : null}
                key={value}
                checked={this.state.pickupTime === value}
                id={value + this.state.idSuffix}
                label={value}
                value={value}
                variant='base'
              />
            ))}
          </RadioGroup>
      </div>
    );
  }

  renderMealSelector() {
    const MealSelector = this.props.accessible ? AccessibleCombobox : InaccessibleCombobox;
    const id = (this.props.accessible ? 'accessible-meal-' : 'inaccessible-meal-') + this.state.idSuffix;
    return (
      <div className='pts'>
        <MealSelector
          id={id}
          ref={this.meal}
          errorText='Meal selection is required'
          label='Meal'
          placeholder='Select an Option'
          showError={this.state.showMealError}
          options={mealOptions}
          required={true}
        />
      </div>
    );
  }

  renderDrinkSelector() {
    const DrinkSelector = this.props.accessible ? AccessibleCombobox : InaccessibleCombobox;
    const id = (this.props.accessible ? 'accessible-drink-' : 'inaccessible-drink-') + this.state.idSuffix;
    return (
      <div className='pts'>
        <DrinkSelector
          id={id}
          ref={this.drink}
          errorText='Drink selection is required'
          label='Drink'
          placeholder='Select an Option'
          showError={this.state.showDrinkError}
          options={drinkOptions}
          required={true}
        />
        <p className='ptxs slds-text-title'>Note: Bring your reusable bottle to fill up on water!</p>
      </div>
    )
  }

  renderDessertSelector() {
    const DessertSelector = this.props.accessible ? AccessibleCombobox : InaccessibleCombobox;
    const id = (this.props.accessible ? 'accessible-dessert-' : 'inaccessible-dessert-') + this.state.idSuffix;
    return (
      <div className='pts'>
        <DessertSelector
          id={id}
          label='Dessert'
          placeholder='Select an Option'
          options={dessertOptions}
        />
      </div>
    )  
  }

  submit() {
    // This is the hackiest way to do this. Sorry not sorry.
    const hasDrinkError = !ReactDOM.findDOMNode(this.drink.current).querySelector('input').value;
    const hasMealError = !ReactDOM.findDOMNode(this.meal.current).querySelector('input').value;
    const valid = !hasDrinkError && !hasMealError;

    this.setState({
      showDrinkError: hasDrinkError,
      showMealError: hasMealError
    });

    if (valid || !this.props.accessible) {
      this.setState({finished: true});
      this.props.nextFunc();
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Escape') {
      this.setState({focusTrap: false});
    } else if (e.key === 'Tab' && this.state.focusTrap) {
      const first = ReactDOM.findDOMNode(this.selectedRadio.current).querySelector('input');
      const last = ReactDOM.findDOMNode(this.continueButton.current)    ;
      if (e.shiftKey) {
        if (e.target === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (e.target === last) {
          first.focus();
          e.preventDefault(); 
        }
      }
    }
  }

  render() {
    return (
      <BrowserContainer label={`Lunch at ${CONFERENCE_NAME}`}>
        <div className='bg-lightGray' onKeyDown={this.handleKeyDown}>
          <div className='slds-clearfix horz-center slds-size_2-of-3 bg-white'>

            <img className='slds-align_absolute-center slds-size_4-of-4' alt='' src='assets/images/lunchBanner.png'/>
            <h2 className='slds-align_absolute-center slds-size_4-of-4 pam text-white slds-text-heading_medium' style={{backgroundColor: '#274301'}}>Lunch Sign-Up</h2>
            <p className='ptm slds-align_absolute-center'>
              Joining us for lunch? Select your preferences below!
            </p>

            <div className='no-pointer-events pvs phl'>
              {this.renderPickupTime()}
              {this.renderMealSelector()}
              {this.renderDrinkSelector()}
              {this.renderDessertSelector()}
              <Textarea classNameContainer='ptm' id={'dietary-' + this.state.idSuffix} label='Any allergies or dietary constraints?' />
            </div>

            <div className='slds-grid phl pvs'>
              <div className='slds-col slds-size_4-of-12'></div>
              <div className='slds-col slds-size_4-of-12'>
                <Button
                  ref={this.continueButton}
                  disabled={this.state.finished ? true : null}
                  label='Continue'
                  onClick={this.submit}
                  variant='brand'
                  className='mtl slds-align_absolute-center'
                />
              </div>
              <div className='slds-col slds-size_4-of-12 slds-text-align_right'>
                <img alt='Chef Astro' src='assets/images/chefAstro.png' className='slds-size_2-of-4' />
              </div>
            </div>

          </div>
        </div>
      </BrowserContainer>
    );
  }
}

export default LunchSelectorScreen;



// WEBPACK FOOTER //
// ./src/steps/keyboard/lunchSelectorScreen.js